import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, Badge } from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { companiesApi, usersApi, productsApi, bankGatewayApi, operationsApi, depositsApi, positionsApi, applicationsApi, usdcTradesApi, providerAccountApi, hedgesAdminApi } from "@/services/api";
import { Wallet, Package, LayoutDashboard, Star, Loader2, ArrowUpRight, ArrowDownRight, Landmark, RefreshCw, Banknote, PiggyBank, Scale, TrendingUp, TrendingDown, ArrowRightLeft, DollarSign, AlertTriangle, Clock, CheckCircle2, XCircle, ChevronLeft, ChevronRight, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth.store";
import { formatCurrency, formatDate } from "@/lib/utils";
import type { HedgeDashboard } from "@/services/api";

interface ProductSummary {
  id: string;
  name: string;
  cdiRate: string;
  gracePeriodDays: number;
  isDefault: boolean;
  volume: number;
  positionsCount: number;
}

interface BankTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  amountBRL: string;
  type: "credit" | "debit";
  accountName: string;
}

interface BankDashboardSummary {
  totalBalance: number;
  totalBalanceBRL: string;
  accounts: Array<{
    id: string;
    bankName: string;
    agency: string;
    accountNumber: string;
    balance: number;
    balanceBRL: string;
    lastUpdated: string | null;
  }>;
  lastTransactions: BankTransaction[];
}

interface ApplicationsSummary {
  totalAppliedCents: number;
  totalAppliedBRL: string;
  totalRedeemedCents: number;
  totalRedeemedBRL: string;
  currentBalanceCents: number;
  currentBalanceBRL: string;
  byType: Record<string, { count: number; totalCents: number }>;
}

interface DepositStats {
  totalMintedBRL: number;
  minted: number;
  pending: number;
  failed: number;
}

interface UsdcTradeStats {
  total: number;
  pending: number;
  processing: number;
  settled: number;
  completed: number;
  failed: number;
  expired: number;
  todayBrlVolume: number;
  todayBrlVolumeFormatted: string;
  todayUsdcVolume: number;
  todayUsdcVolumeFormatted: string;
  divergent: number;
}

interface ProviderAccountSummary {
  providerCode: string;
  displayName: string;
  brlBalance: number;
  brlBalanceFormatted: string;
  usdBalance: number;
  usdBalanceFormatted: string;
  lastSyncAt?: string;
  syncStatus?: string;
  alerts: {
    lowBrlBalance: boolean;
    lowUsdBalance: boolean;
  };
}

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    pendingCompanies: 0,
    totalUsers: 0,
  });
  const [productsSummary, setProductsSummary] = useState<{
    products: ProductSummary[];
    totalVolume: number;
    totalProducts: number;
  } | null>(null);
  const [bankSummary, setBankSummary] = useState<BankDashboardSummary | null>(null);
  const [applicationsSummary, setApplicationsSummary] = useState<ApplicationsSummary | null>(null);
  const [depositStats, setDepositStats] = useState<DepositStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingBank, setLoadingBank] = useState(true);
  const [refreshingBank, setRefreshingBank] = useState(false);
  const [spreadData, setSpreadData] = useState<{
    positionsYield: number;
    applicationsYield: number;
    spread: number;
  } | null>(null);
  const [loadingSpread, setLoadingSpread] = useState(true);
  const [usdcTradeStats, setUsdcTradeStats] = useState<UsdcTradeStats | null>(null);
  const [providerAccounts, setProviderAccounts] = useState<ProviderAccountSummary[]>([]);
  const [activeProviderIndex, setActiveProviderIndex] = useState(0);
  const [loadingUsdc, setLoadingUsdc] = useState(true);
  const [hedgeDashboard, setHedgeDashboard] = useState<HedgeDashboard | null>(null);
  const [loadingHedge, setLoadingHedge] = useState(true);

  // Calcular valores de lastro
  const bankBalance = bankSummary?.totalBalance || 0;
  const applicationsBalance = applicationsSummary?.currentBalanceCents || 0;
  const totalBackingCents = bankBalance + applicationsBalance;
  // Lastro em Tesouro = percentual do total que está efetivamente aplicado (não em conta)
  const treasuryBackingPercentage = totalBackingCents > 0 ? Math.round((applicationsBalance / totalBackingCents) * 100) : 0;

  useEffect(() => {
    async function loadStats() {
      try {
        const [companies, users] = await Promise.all([
          companiesApi.list(),
          usersApi.list(),
        ]);

        setStats({
          totalCompanies: companies.length,
          activeCompanies: companies.filter((c: any) => c.status === "ACTIVE").length,
          pendingCompanies: companies.filter((c: any) => c.status === "PENDING").length,
          totalUsers: users.length,
        });
      } catch (error) {
        console.error("Erro ao carregar estatísticas:", error);
      } finally {
        setLoading(false);
      }
    }

    async function loadProductsSummary() {
      try {
        const summary = await productsApi.getSummary();
        setProductsSummary(summary);
      } catch (error) {
        console.error("Erro ao carregar resumo de produtos:", error);
      } finally {
        setLoadingProducts(false);
      }
    }

    async function loadBankSummary() {
      try {
        const [bankData, appsData, depStats] = await Promise.all([
          bankGatewayApi.getDashboardSummary(),
          operationsApi.getApplicationsSummary().catch(() => null),
          depositsApi.getStats().catch(() => null),
        ]);
        setBankSummary(bankData);
        if (appsData) {
          setApplicationsSummary(appsData);
        }
        if (depStats) {
          setDepositStats(depStats);
        }
      } catch (error) {
        console.error("Erro ao carregar resumo bancário:", error);
      } finally {
        setLoadingBank(false);
      }
    }

    async function loadSpreadData() {
      try {
        const [positionsSummary, yieldSummary] = await Promise.all([
          positionsApi.getGlobalSummary(),
          applicationsApi.getYieldSummary(),
        ]);

        const positionsYield = positionsSummary.totalYieldCents;
        const applicationsYield = yieldSummary.totalYieldCents;

        setSpreadData({
          positionsYield,
          applicationsYield,
          spread: applicationsYield - positionsYield,
        });
      } catch (error) {
        console.error("Erro ao carregar spread:", error);
      } finally {
        setLoadingSpread(false);
      }
    }

    async function loadUsdcData() {
      try {
        const [trades, accounts] = await Promise.all([
          usdcTradesApi.getStats().catch(() => null),
          providerAccountApi.listProviderAccounts().catch(() => []),
        ]);
        if (trades) setUsdcTradeStats(trades);
        if (accounts && accounts.length > 0) setProviderAccounts(accounts);
      } catch (error) {
        console.error("Erro ao carregar dados USDC:", error);
      } finally {
        setLoadingUsdc(false);
      }
    }

    async function loadHedgeData() {
      try {
        const dashboard = await hedgesAdminApi.getDashboard().catch(() => null);
        if (dashboard) setHedgeDashboard(dashboard);
      } catch (error) {
        console.error("Erro ao carregar dados de proteção cambial:", error);
      } finally {
        setLoadingHedge(false);
      }
    }

    loadStats();
    loadProductsSummary();
    loadBankSummary();
    loadSpreadData();
    loadUsdcData();
    loadHedgeData();
  }, []);

  async function handleRefreshBank() {
    setRefreshingBank(true);
    try {
      const [bankData, appsData, depStats] = await Promise.all([
        bankGatewayApi.getDashboardSummary(),
        operationsApi.getApplicationsSummary().catch(() => null),
        depositsApi.getStats().catch(() => null),
      ]);
      setBankSummary(bankData);
      if (appsData) {
        setApplicationsSummary(appsData);
      }
      if (depStats) {
        setDepositStats(depStats);
      }
    } catch (error) {
      console.error("Erro ao atualizar resumo bancário:", error);
    } finally {
      setRefreshingBank(false);
    }
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<LayoutDashboard className="w-5 h-5 sm:w-6 sm:h-6" />}
        title={`Bem-vindo, ${user?.name?.split(" ")[0] || "Admin"}!`}
        description="Visão geral do sistema BRLD"
        stats={[
          { value: loading ? "..." : stats.totalCompanies, label: "Empresas" },
          { value: loading ? "..." : stats.activeCompanies, label: "Ativas" },
          { value: loading ? "..." : stats.pendingCompanies, label: "Pendentes" },
          { value: loading ? "..." : stats.totalUsers, label: "Usuários" },
        ]}
      />

      {/* Volume e Produtos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Volume BRLD
            </CardTitle>
            <button
              onClick={handleRefreshBank}
              disabled={refreshingBank}
              className="p-1.5 rounded-md hover:bg-muted transition-colors disabled:opacity-50"
              title="Atualizar saldo"
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${refreshingBank ? "animate-spin" : ""}`} />
            </button>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingBank ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* BRLD Emitido (soma dos mints realizados) */}
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">BRLD Emitido</span>
                  <span className="text-xl sm:text-2xl font-bold">
                    {depositStats ? formatCurrency(depositStats.totalMintedBRL) : "R$ 0,00"}
                  </span>
                </div>

                {/* Saldo Bancário */}
                <div className="flex justify-between items-center border-t pt-3">
                  <div className="flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Saldo em Conta</span>
                  </div>
                  <span className="text-lg sm:text-xl font-bold text-success">
                    {bankSummary?.totalBalanceBRL || "R$ 0,00"}
                  </span>
                </div>

                {/* Detalhes das contas */}
                {bankSummary?.accounts && bankSummary.accounts.length > 0 && (
                  <div className="pl-6 space-y-1">
                    {bankSummary.accounts.map((account) => (
                      <div key={account.id} className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">
                          {account.bankName} ({account.agency}/{account.accountNumber})
                        </span>
                        <span className="font-medium">{account.balanceBRL}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Saldo Aplicado */}
                <div className="flex justify-between items-center border-t pt-3">
                  <div className="flex items-center gap-2">
                    <PiggyBank className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Saldo Aplicado</span>
                  </div>
                  <span className="text-lg sm:text-xl font-bold text-blue-500">
                    {applicationsSummary?.currentBalanceBRL || "R$ 0,00"}
                  </span>
                </div>

                {/* Detalhes por tipo de aplicação */}
                {applicationsSummary?.byType && Object.keys(applicationsSummary.byType).length > 0 && (
                  <div className="pl-6 space-y-1">
                    {Object.entries(applicationsSummary.byType).map(([type, data]) => (
                      <div key={type} className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">
                          {type} ({data.count})
                        </span>
                        <span className="font-medium">{formatCurrency(data.totalCents / 100)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Total do Lastro e Percentual */}
                <div className="flex justify-between items-center border-t pt-3">
                  <span className="text-muted-foreground font-medium">Total Lastro</span>
                  <span className="text-lg sm:text-xl font-bold">
                    {formatCurrency(totalBackingCents / 100)}
                  </span>
                </div>

                <div className="flex justify-between items-center border-t pt-3">
                  <span className="text-muted-foreground">Lastro em Tesouro</span>
                  <span className={`text-lg font-medium ${treasuryBackingPercentage >= 100 ? "text-success" : "text-destructive"}`}>
                    {treasuryBackingPercentage}%
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Produtos de Investimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingProducts ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : productsSummary && productsSummary.products.length > 0 ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="text-muted-foreground text-sm">Volume Total Aplicado</span>
                  <span className="text-lg sm:text-xl font-bold text-success">
                    {formatCurrency(productsSummary.totalVolume)}
                  </span>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {productsSummary.products.map((product) => (
                    <div
                      key={product.id}
                      className="flex justify-between items-center py-1.5 px-2 rounded-md hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{product.name}</span>
                        {product.isDefault && (
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                        )}
                        <span className="text-xs text-muted-foreground">
                          ({product.cdiRate}% CDI)
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-medium">
                          {formatCurrency(product.volume)}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">
                          ({product.positionsCount} pos.)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhum produto ativo</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Card de Spread de Rentabilidade */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            Spread de Rentabilidade
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingSpread ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : spreadData ? (
            <div className="space-y-4">
              {/* Grid lado a lado */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Lado PASSIVO */}
                <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingDown className="w-5 h-5 text-destructive" />
                    <span className="font-semibold text-destructive">PASSIVO</span>
                    <span className="text-sm text-muted-foreground">(Posições)</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xl sm:text-2xl font-bold">
                      {formatCurrency(spreadData.positionsYield / 100)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Rentabilidade devida às empresas
                    </p>
                  </div>
                </div>

                {/* Lado ATIVO */}
                <div className="p-4 rounded-lg bg-success/5 border border-success/20">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-success" />
                    <span className="font-semibold text-success">ATIVO</span>
                    <span className="text-sm text-muted-foreground">(Aplicações)</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xl sm:text-2xl font-bold">
                      {formatCurrency(spreadData.applicationsYield / 100)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Rentabilidade das aplicações
                    </p>
                  </div>
                </div>
              </div>

              {/* Resultado do Spread */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-lg font-medium text-muted-foreground">SPREAD:</span>
                  <span className={`text-3xl font-bold ${spreadData.spread >= 0 ? "text-success" : "text-destructive"}`}>
                    {spreadData.spread >= 0 ? "+" : ""}{formatCurrency(spreadData.spread / 100)}
                  </span>
                  <Badge variant={spreadData.spread >= 0 ? "success" : "destructive"}>
                    {spreadData.spread >= 0 ? "Margem positiva" : "Margem negativa"}
                  </Badge>
                </div>
                <p className="text-center text-sm text-muted-foreground mt-2">
                  {spreadData.spread >= 0
                    ? "Ganhos das aplicações cobrem as obrigações com empresas"
                    : "Atenção: obrigações com empresas excedem ganhos das aplicações"
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Scale className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Não foi possível carregar dados do spread</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Operações USDC e Conta CAInvest */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Card Operações USDC */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              Operações USDC
            </CardTitle>
            <button
              onClick={() => navigate("/admin/usdc-trades")}
              className="text-xs text-primary hover:underline"
            >
              Ver todas
            </button>
          </CardHeader>
          <CardContent>
            {loadingUsdc ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : usdcTradeStats ? (
              <div className="space-y-4">
                {/* Volume de Hoje */}
                <div className="flex justify-between items-center border-b pb-3">
                  <span className="text-muted-foreground">Volume Hoje</span>
                  <span className="text-lg font-bold text-success">
                    {usdcTradeStats.todayBrlVolumeFormatted}
                  </span>
                </div>

                {/* Status das trades */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm">Pendentes</span>
                    </div>
                    <Badge variant="secondary">{usdcTradeStats.pending}</Badge>
                  </div>

                  <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-blue-500" />
                      <span className="text-sm">Processando</span>
                    </div>
                    <Badge variant="default">{usdcTradeStats.processing}</Badge>
                  </div>

                  <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Concluídas</span>
                    </div>
                    <Badge variant="success">{usdcTradeStats.completed}</Badge>
                  </div>

                  <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-sm">Falhas</span>
                    </div>
                    <Badge variant="destructive">{usdcTradeStats.failed}</Badge>
                  </div>
                </div>

                {/* Alertas */}
                {(usdcTradeStats.pending > 5 || usdcTradeStats.divergent > 0 || usdcTradeStats.failed > 0) && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        {usdcTradeStats.divergent > 0 && (
                          <p>{usdcTradeStats.divergent} trade(s) com divergência</p>
                        )}
                        {usdcTradeStats.pending > 5 && (
                          <p>{usdcTradeStats.pending} trades pendentes</p>
                        )}
                        {usdcTradeStats.failed > 0 && (
                          <p>{usdcTradeStats.failed} trade(s) com falha</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <ArrowRightLeft className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Nenhuma operação USDC</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Card Contas de Provedores (Carousel) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              {providerAccounts.length > 0
                ? `Conta ${providerAccounts[activeProviderIndex]?.displayName || "Provedor"}`
                : "Contas de Provedores"
              }
            </CardTitle>
            <button
              onClick={() => navigate("/admin/providers")}
              className="text-xs text-primary hover:underline"
            >
              Ver detalhes
            </button>
          </CardHeader>
          <CardContent>
            {loadingUsdc ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : providerAccounts.length > 0 ? (
              <div className="space-y-4">
                {/* Saldos do provedor ativo */}
                {(() => {
                  const account = providerAccounts[activeProviderIndex];
                  if (!account) return null;
                  return (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">Saldo BRL</p>
                          <p className="text-xl font-bold">{account.brlBalanceFormatted}</p>
                          {account.alerts?.lowBrlBalance && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Abaixo do mínimo
                            </p>
                          )}
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">Saldo USD</p>
                          <p className="text-xl font-bold">{account.usdBalanceFormatted}</p>
                          {account.alerts?.lowUsdBalance && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Abaixo do mínimo
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Alertas de saldo baixo */}
                      {(account.alerts?.lowBrlBalance || account.alerts?.lowUsdBalance) && (
                        <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                            <div className="text-sm text-red-800 dark:text-red-300">
                              <p className="font-medium">Saldo baixo na conta {account.displayName}</p>
                              <p className="text-xs mt-1">
                                Realize um depósito para garantir liquidez nas operações.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Navegação do carousel (se mais de 1 provedor) */}
                {providerAccounts.length > 1 && (
                  <div className="flex items-center justify-center gap-3 pt-2 border-t">
                    <button
                      onClick={() => setActiveProviderIndex((prev) => prev === 0 ? providerAccounts.length - 1 : prev - 1)}
                      className="p-1 rounded-full hover:bg-muted transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                    </button>
                    <div className="flex gap-1.5">
                      {providerAccounts.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveProviderIndex(idx)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            idx === activeProviderIndex
                              ? "bg-primary"
                              : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                          }`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => setActiveProviderIndex((prev) => prev === providerAccounts.length - 1 ? 0 : prev + 1)}
                      className="p-1 rounded-full hover:bg-muted transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Nenhum provedor configurado</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Proteção Cambial */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Proteção Cambial
          </CardTitle>
          <button
            onClick={() => navigate("/admin/hedges")}
            className="text-xs text-primary hover:underline"
          >
            Ver todas
          </button>
        </CardHeader>
        <CardContent>
          {loadingHedge ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : hedgeDashboard ? (
            <div className="space-y-4">
              {/* Métricas principais */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground mb-1">Hedges Ativos</p>
                  <p className="text-2xl font-bold">{hedgeDashboard.activeHedgesCount}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground mb-1">Volume Protegido</p>
                  <p className="text-lg font-bold">
                    $ {Number(hedgeDashboard.totalVolumeUsdc || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground mb-1">Garantias</p>
                  <p className="text-lg font-bold">
                    $ {Number(hedgeDashboard.totalGuaranteesUsdc || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground mb-1">Receita Projetada</p>
                  <p className="text-lg font-bold text-success">
                    {formatCurrency(Number(hedgeDashboard.projectedRevenueBrl || 0))}
                  </p>
                </div>
              </div>

              {/* Linha secundária: receita realizada + alertas */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Receita Realizada</span>
                  </div>
                  <span className="text-sm font-bold">
                    {formatCurrency(Number(hedgeDashboard.realizedRevenueBrl || 0))}
                  </span>
                </div>

                <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm">Aguardando Aprovação</span>
                  </div>
                  <Badge variant="secondary">{hedgeDashboard.pendingApprovalCount}</Badge>
                </div>

                <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-sm">Em Risco</span>
                  </div>
                  <Badge variant={hedgeDashboard.hedgesAtRiskCount > 0 ? "destructive" : "secondary"}>
                    {hedgeDashboard.hedgesAtRiskCount}
                  </Badge>
                </div>
              </div>

              {/* Alerta se há hedges pendentes de aprovação */}
              {hedgeDashboard.pendingApprovalCount > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-amber-600 mt-0.5" />
                    <div className="text-sm text-amber-800 dark:text-amber-300">
                      <p>{hedgeDashboard.pendingApprovalCount} hedge(s) aguardando sua aprovação</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <Shield className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Nenhum dado de proteção cambial</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Últimas Operações Bancárias */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5" />
            Últimas Operações Bancárias
          </CardTitle>
          <Badge variant="secondary" className="text-xs">
            Últimos 30 dias
          </Badge>
        </CardHeader>
        <CardContent>
          {loadingBank ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : bankSummary?.lastTransactions && bankSummary.lastTransactions.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {bankSummary.lastTransactions.map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-muted/50 border-b border-border last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        tx.type === "credit"
                          ? "bg-success/10 text-success"
                          : "bg-destructive/10 text-destructive"
                      }`}
                    >
                      {tx.type === "credit" ? (
                        <ArrowDownRight className="w-4 h-4" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{tx.description || "Operação bancária"}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(tx.date)} • {tx.accountName}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`font-semibold ${
                        tx.type === "credit" ? "text-success" : "text-destructive"
                      }`}
                    >
                      {tx.type === "credit" ? "+" : "-"} {tx.amountBRL}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Banknote className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Nenhuma operação nos últimos 30 dias</p>
              <p className="text-xs mt-1">Configure uma conta bancária para ver as operações</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
